import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Plus, Edit, Trash2, Loader2, FileText, Layout,
} from 'lucide-react';
import { toast } from 'sonner';

import { SectionEditorDialog } from './admin-cms/SectionEditorDialog';
import { PageCard } from './admin-cms/PageCard';
import { PageEditorDialog } from './admin-cms/PageEditorDialog';
import { ImageUploadField } from './admin-cms/ImageUploadField';
import { DEFAULT_PAGES, SECTION_TYPES, EMPTY_SECTION_FORM } from './admin-cms/constants';

/**
 * AdminCMSPage — top-level orchestrator for the block-based CMS.
 *
 * Layout & sub-components live in `./admin-cms/`:
 *   • PageCard           — single card on the Pages tab
 *   • PageEditorDialog   — modal for editing page meta + blocks
 *   • SectionEditorDialog — block-type-specific editor
 *   • PageBuilder        — in-dialog block drag/sort/preview
 *   • ImageUploadField   — reusable URL + upload control
 *   • constants          — DEFAULT_PAGES, SECTION_TYPES, EMPTY_SECTION_FORM
 *
 * State + API calls (pages CRUD, sections CRUD) stay here so the dialogs
 * remain thin presentational shells.
 */
export function AdminCMSPage() {
  const { api } = useAuth();
  const [pages, setPages] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pages');

  // Dialog state
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingForPageId, setEditingForPageId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [builderKey, setBuilderKey] = useState(0);

  const [pageForm, setPageForm] = useState({
    slug: '', title: '', description: '', meta_title: '', meta_description: '',
    is_published: true, sections: [],
  });
  const [sectionForm, setSectionForm] = useState(EMPTY_SECTION_FORM);

  const fetchData = useCallback(async () => {
    try {
      const [pagesRes, sectionsRes] = await Promise.all([
        api.get('/api/cms/admin/pages'),
        api.get('/api/cms/sections'),
      ]);
      setPages(pagesRes.data.pages || []);
      setSections(sectionsRes.data.sections || []);
    } catch (error) {
      console.error('Failed to fetch CMS data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Page CRUD ──────────────────────────────────────────────────────────
  const initializeDefaultPages = async () => {
    setSaving(true);
    try {
      for (const dp of DEFAULT_PAGES) {
        if (!pages.find(p => p.slug === dp.slug)) {
          await api.post('/api/cms/pages', {
            id: '', slug: dp.slug, title: dp.title, description: dp.description,
            is_published: true, sections: [],
          });
        }
      }
      fetchData();
    } catch (error) {
      console.error('Failed to initialize pages:', error);
    } finally {
      setSaving(false);
    }
  };

  const openPageDialog = (page = null) => {
    if (page && page.id) {
      setEditingPage(page);
      setPageForm({
        slug: page.slug, title: page.title,
        description: page.description || '',
        meta_title: page.meta_title || '',
        meta_description: page.meta_description || '',
        is_published: page.is_published,
        sections: page.sections || [],
      });
    } else {
      setEditingPage(null);
      setPageForm({
        slug: page?.slug || '', title: page?.title || '',
        description: page?.description || '',
        meta_title: '', meta_description: '',
        is_published: true, sections: [],
      });
    }
    setPageDialogOpen(true);
  };

  const savePageMeta = async () => {
    setSaving(true);
    try {
      let pageId = editingPage?.id;
      if (editingPage) {
        await api.put(`/api/cms/pages/${editingPage.id}`, { ...pageForm, id: editingPage.id });
        toast.success('Page updated');
      } else {
        const { data } = await api.post('/api/cms/pages', { ...pageForm, id: '' });
        pageId = data.page?.id;
        setEditingPage(data.page);
        toast.success('Page created — now add blocks below');
      }
      await fetchData();
      setBuilderKey(k => k + 1);
      return pageId;
    } catch (error) {
      console.error('Failed to save page:', error);
      toast.error(error.response?.data?.detail || 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Delete this page? Blocks will remain in the library.')) return;
    try {
      await api.delete(`/api/cms/pages/${pageId}`);
      toast.success('Page deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete page:', error);
    }
  };

  // ─── Section / Block CRUD ───────────────────────────────────────────────
  const openSectionDialog = (section = null, ctx = {}) => {
    setEditingForPageId(ctx.pageId || null);
    if (section) {
      setEditingSection(section);
      setSectionForm({
        ...EMPTY_SECTION_FORM, ...section,
        items: section.items || [], columns: section.columns || [],
      });
    } else {
      setEditingSection(null);
      setSectionForm({ ...EMPTY_SECTION_FORM, order: sections.length });
    }
    setSectionDialogOpen(true);
  };

  const saveSection = async () => {
    setSaving(true);
    try {
      const payload = { ...sectionForm };
      delete payload.items_json;
      if (editingSection) {
        await api.put(`/api/cms/sections/${editingSection.id}`, { ...payload, id: editingSection.id });
        toast.success('Block saved');
      } else if (editingForPageId) {
        await api.post(`/api/cms/pages/${editingForPageId}/sections`, { ...payload, id: '' });
        toast.success('Block added to page');
      } else {
        await api.post('/api/cms/sections', { ...payload, id: '' });
        toast.success('Block created in library');
      }
      setSectionDialogOpen(false);
      setBuilderKey(k => k + 1);
      fetchData();
    } catch (error) {
      console.error('Failed to save section:', error);
      toast.error(error.response?.data?.detail || 'Failed to save block');
    } finally {
      setSaving(false);
    }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('Delete this block permanently?')) return;
    try {
      await api.delete(`/api/cms/sections/${sectionId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete section:', error);
    }
  };

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">CMS Page Builder</h1>
            <p className="text-[#A0B4CC] mt-1">Build pages block-by-block — Rich text, HTML, images, video, columns, FAQ, pricing, CTA…</p>
          </div>
          <div className="flex gap-3">
            {pages.length === 0 && (
              <Button onClick={initializeDefaultPages} disabled={saving} className="btn-secondary" data-testid="init-default-pages-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Initialize Default Pages
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#041A3B] border border-white/10 mb-6">
            <TabsTrigger value="pages" className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-[#000B18]">
              <FileText className="w-4 h-4 mr-2" />
              Pages ({pages.length})
            </TabsTrigger>
            <TabsTrigger value="sections" className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-[#000B18]">
              <Layout className="w-4 h-4 mr-2" />
              Block Library ({sections.length})
            </TabsTrigger>
          </TabsList>

          {/* Pages tab */}
          <TabsContent value="pages">
            <div className="flex justify-end mb-4">
              <Button onClick={() => openPageDialog()} className="btn-primary" data-testid="add-page-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Page
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {DEFAULT_PAGES.map((dp) => {
                const page = pages.find(p => p.slug === dp.slug);
                return (
                  <PageCard
                    key={dp.slug} page={page} title={dp.title} description={dp.description}
                    slug={dp.slug} Icon={dp.icon}
                    onEdit={() => openPageDialog(page || dp)} color="#00E5FF"
                  />
                );
              })}
              {pages.filter(p => !DEFAULT_PAGES.find(d => d.slug === p.slug)).map((page) => (
                <PageCard
                  key={page.id} page={page} title={page.title} description={page.description}
                  slug={page.slug} Icon={FileText}
                  onEdit={() => openPageDialog(page)}
                  onDelete={() => deletePage(page.id)} color="#007AFF"
                />
              ))}
            </div>
          </TabsContent>

          {/* Block-library tab */}
          <TabsContent value="sections">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[#A0B4CC] text-sm">All blocks in the system. Re-use across pages by duplicating, or edit in place.</p>
              <Button onClick={() => openSectionDialog()} className="btn-primary" data-testid="add-section-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Block
              </Button>
            </div>
            <div className="space-y-3">
              {sections.length === 0 ? (
                <Card className="bg-[#041A3B] border-white/10">
                  <CardContent className="py-12 text-center">
                    <Layout className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
                    <p className="text-[#A0B4CC]">No blocks yet — open a page and add blocks there, or create one here.</p>
                  </CardContent>
                </Card>
              ) : (
                sections.map((section) => (
                  <Card key={section.id} className="bg-[#041A3B] border-white/10">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-[#00E5FF]/10 flex items-center justify-center">
                          <Layout className="w-5 h-5 text-[#00E5FF]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className="bg-[#007AFF]/20 text-[#007AFF] text-[10px] uppercase">{section.section_type}</Badge>
                            {section.is_visible === false && (
                              <Badge className="bg-[#5C7599]/30 text-[#A0B4CC] text-[10px]">Hidden</Badge>
                            )}
                          </div>
                          <p className="text-white text-sm font-medium truncate">{section.title || 'Untitled block'}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openSectionDialog(section)} className="text-[#00E5FF]">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteSection(section.id)} className="text-[#FF1744]">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Page editor modal */}
        <PageEditorDialog
          open={pageDialogOpen}
          onOpenChange={(o) => { setPageDialogOpen(o); if (!o) setEditingPage(null); }}
          editingPage={editingPage}
          pageForm={pageForm}
          setPageForm={setPageForm}
          saving={saving}
          savePageMeta={savePageMeta}
          api={api}
          builderKey={builderKey}
          onEditBlock={openSectionDialog}
        />

        {/* Block editor modal */}
        <SectionEditorDialog
          open={sectionDialogOpen}
          onOpenChange={setSectionDialogOpen}
          sectionForm={sectionForm}
          setSectionForm={setSectionForm}
          saveSection={saveSection}
          saving={saving}
          editingSection={editingSection}
          sectionTypes={SECTION_TYPES}
          api={api}
        />
      </div>
    </AdminSidebarLayout>
  );
}

// Re-export for backward compatibility — external imports keep working.
export { ImageUploadField };
